import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Phone } from "lucide-react";

const MaskLine = ({ children, delay = 0 }) => (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const bookCall = () =>
    window.dispatchEvent(new Event("savv:book-call"));

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const bgY = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section
            id="top"
            data-testid="hero-section"
            ref={ref}
            className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 md:pt-32 bg-[#000000]"
        >
            <motion.div
                style={{ y: bgY }}
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-32 w-[46rem] h-[46rem] rounded-full bg-[#D3A0A7] opacity-[0.07] blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[-8rem] left-[-10rem] w-[36rem] h-[36rem] rounded-full bg-[#D3A0A7] opacity-[0.04] blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-center justify-start pl-[2vw] overflow-hidden"
            >
                <span className="text-stroke-ghost font-serif-ed font-bold text-[26vw] leading-none select-none whitespace-nowrap">
                    SAVV
                </span>
            </div>

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#0C0C0C] bg-[#D3A0A7] rounded-full px-4 py-2 mb-8"
                    >
                        <Sparkles size={13} />
                        Creative Design + Marketing
                    </motion.p>

                    <h1 className="font-serif-ed text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#F1F1EF]">
                        <MaskLine delay={0.2}>Make your</MaskLine>
                        <MaskLine delay={0.32}>brand look</MaskLine>
                        <MaskLine delay={0.44}>
                            <span className="italic text-[#D3A0A7]">Savv.</span>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        className="mt-7 max-w-md text-base md:text-lg leading-relaxed text-[#A9A6A0]"
                    >
                        Creative design + marketing for all businesses — big
                        and small — ready to stand out, connect with their
                        audience, and grow.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contact"
                            onClick={bookCall}
                            data-testid="hero-book-call-btn"
                            className="group inline-flex items-center gap-2 bg-[#D3A0A7] text-[#0C0C0C] font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-[#F1F1EF] transition-colors duration-300"
                        >
                            Book a Call
                            <ArrowRight
                                size={16}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </a>
                        <a
                            href="#work"
                            data-testid="hero-view-work-btn"
                            className="inline-flex items-center gap-2 border border-[#F1F1EF]/30 text-[#F1F1EF] font-semibold text-sm px-7 py-3.5 rounded-full hover:border-[#D3A0A7] hover:text-[#D3A0A7] transition-colors duration-300"
                        >
                            View Our Work
                            <ArrowUpRight size={16} />
                        </a>
                        <a
                            href="tel:+14175904345"
                            data-testid="hero-call-btn"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#CFCCC6] link-underline px-2 py-3.5"
                        >
                            <Phone size={16} />
                            (417) 590-4345
                        </a>
                        <span className="font-hand text-xl text-[#D3A0A7] rotate-[-2deg] px-1">
                            call or text anytime ♡
                        </span>
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 50, rotate: 3 }}
                        animate={{ opacity: 1, y: 0, rotate: 2 }}
                        transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <motion.div
                            style={{ y: imgY }}
                            className="relative rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-24px_rgba(12,12,12,0.35)] border-4 border-[#F1F1EF]"
                        >
                            <img
                                src="/images/soft-hero-collage.png"
                                alt="Savv Media brand mockup collage — website, social grid and stationery"
                                data-testid="hero-collage-image"
                                className="w-full h-auto block"
                            />
                        </motion.div>

                        <div
                            className="float-soft absolute -top-6 -left-4 md:-left-10 bg-[#F1F1EF] rounded-2xl shadow-lg border border-[#262626] px-4 py-3 rotate-[-2deg]"
                            style={{ "--float-rot": "-2deg" }}
                        >
                            <span
                                className="font-hand text-2xl text-[#D3A0A7]"
                                data-testid="hero-handwritten-badge"
                            >
                                big ideas, real results ♡
                            </span>
                        </div>

                        <div
                            className="float-soft absolute -bottom-5 -right-3 md:-right-8 bg-[#262626] text-[#F1F1EF] rounded-2xl shadow-xl px-5 py-3.5 rotate-[1deg]"
                            style={{ "--float-rot": "1deg", animationDelay: "1.2s" }}
                        >
                            <p className="text-[11px] uppercase tracking-[0.18em] opacity-80">
                                Stop the scroll
                            </p>
                            <p className="font-serif-ed text-xl leading-tight">
                                Brands worth remembering
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
